import { Box, Button, Card, CardBody, CardHeader, Heading, Image, Stack, Text, useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Address, CargoType, DocumentType } from '@pelicargo/types';
import { buildFlightPathString, Show } from '@pelicargo/ui';
import {
  getPrettyEarliestTenderOption,
  isoCountryFallback,
  isoStateFallback,
  join,
  prettyDate,
  prettyDockHour,
} from '@pelicargo/utils';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { CopyButton } from '../../components/CopyButton';
import { CopyLineItemsButton } from '../../components/CopyLineItemsButton';
import { DocumentsForm } from '../../components/Documents/DocumentsForm ';
import { InstructionsCard } from '../../components/InstructionCard';
import { LabeledValue } from '../../components/LabeledValue';
import { QuoteVariantUI } from '../../components/QuoteVariantTabs/QuoteVariant';
import { RequestDetailsCard } from '../../components/RequestDetailsCard';
import { SimpleForm } from '../../components/SimpleForm';
import { SimpleInput } from '../../components/SimpleInput';
import { Timeline } from '../../components/Timeline';
import { Validity } from '../../components/Validity';
import { trpc, TrpcOutputs } from '../../config/trpc';
import { useFlightData } from '../../hooks/useFlightData';

const schema = z.object({
  pickup_ref: z.string().nullish(),
});

type FormSchema = z.infer<typeof schema>;

type Props = {
  quote: TrpcOutputs['getFinalQuote']['quote'];
};

export const EXWFPDashboard = ({ quote }: Props) => {
  const toast = useToast();
  const { mutateAsync: updateQuote, isLoading: isUpdating } = trpc.updateQuote.useMutation();
  const { flightData } = useFlightData(quote?.flight_path);

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;

  const request = quote?.request;
  const methods = useForm<FormSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      pickup_ref: bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref || '',
    },
  });

  useEffect(() => {
    const pickup_ref = bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref || '';
    if (pickup_ref) methods.setValue('pickup_ref', pickup_ref);
  }, [methods, bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref]);

  const inlandQuote = bookedQuoteVariant?.inland_service_quote_variant?.quote;
  const isDgd = request?.cargo_type === CargoType.DangerousGoods;

  const documentTypes = useMemo(() => {
    const suggested: DocumentType[] = [
      DocumentType.SLI,
      DocumentType.SHIPPER_DOCUMENTS,
      DocumentType.AIRWAY_BILL,
      DocumentType.BOL,
      DocumentType.LABELS,
    ];

    if (isDgd) suggested.push(DocumentType.DGD, DocumentType.MSDS);

    return suggested;
  }, [isDgd]);

  const stopCountLabel = useMemo(() => {
    const numStops = quote?.flight_path?.length - 1;

    if (numStops === 0) return 'Nonstop';
    if (numStops === 1) return '1 stop';
    return `${numStops} stops`;
  }, [quote?.flight_path?.length]);

  const handleSubmit = async (values: {
    pickup_ref?: string;
    trucking_ref?: string;
    trucking_name?: string;
    estimated_pickup_at?: Date;
    delivery_address?: Address;
  }) => {
    try {
      await updateQuote({
        quote_id: bookedQuoteVariant?.inland_service_quote_variant?.quote_id,
        pickup_ref: values.pickup_ref,
      });

      toast({
        title: 'Success',
        description: 'Updated successfully.',
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'An error occurred while updating the quote.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const pickupRef = inlandQuote?.pickup_ref || '-';
  const readyDate = prettyDate(inlandQuote?.pickup_ready_at, { timeZone: 'Etc/UTC' });
  const dockHours = [prettyDockHour(inlandQuote?.from_dock_hours), prettyDockHour(inlandQuote?.to_dock_hours)]
    .filter(Boolean)
    .join(' - ');
  const pickUpAddress = inlandQuote?.delivery_address;
  const pickUpAddressText = useMemo(() => {
    return [
      pickUpAddress?.recipient,
      pickUpAddress?.line_one,
      pickUpAddress?.line_two,
      join([
        pickUpAddress?.city,
        isoStateFallback(pickUpAddress?.state),
        pickUpAddress?.zip_code,
        isoCountryFallback(pickUpAddress?.country),
      ]),
    ]
      .filter(Boolean)
      .join('\n');
  }, [pickUpAddress]);
  const dropOffAddress = quote?.delivery_address;
  const dropOffAddressText = useMemo(() => {
    return [
      dropOffAddress?.recipient,
      dropOffAddress?.line_one,
      dropOffAddress?.line_two,
      join([
        dropOffAddress?.city,
        isoStateFallback(dropOffAddress?.state),
        dropOffAddress?.zip_code,
        isoCountryFallback(dropOffAddress?.country),
      ]),
    ]
      .filter(Boolean)
      .join('\n');
  }, [dropOffAddress]);
  const pickupContact = inlandQuote?.inland_dropoff_contact;
  const contactInfo = [pickupContact?.full_name, pickupContact?.phone_number, pickupContact?.email]
    .filter(Boolean)
    .join('\n');
  const earliestTenderOption = getPrettyEarliestTenderOption(quote?.earliest_tender);
  const minColumnWidth = '80px';

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing="6">
      <Stack w="full" spacing="6">
        <Show if={!!quote?.booking_instructions}>
          <InstructionsCard instructions={quote?.booking_instructions} />
        </Show>
        <Card borderRadius="lg" role="group" h="fit-content">
          <CardBody>
            <Stack spacing="5">
              <Stack direction="row">
                <Stack flex="1">
                  <Heading variant="h6">Pick Up</Heading>
                  <LabeledValue label="Ready Date" value={readyDate} defaultValue="-" minColumnWidth={minColumnWidth} />
                  <LabeledValue label="Dock Hours" value={dockHours} defaultValue="-" minColumnWidth={minColumnWidth} />
                  <LabeledValue
                    label="Address"
                    value={pickUpAddressText}
                    defaultValue="-"
                    minColumnWidth={minColumnWidth}
                  />
                  <LabeledValue label="Contact" value={contactInfo} defaultValue="-" minColumnWidth={minColumnWidth} />
                  <LabeledValue label="Pickup Ref" value={pickupRef} defaultValue="-" minColumnWidth={minColumnWidth} />
                  <LabeledValue
                    label="Trucking Co"
                    value={bookedQuoteVariant?.inland_service_quote_variant?.airline_ref}
                    defaultValue="-"
                    minColumnWidth={minColumnWidth}
                  />
                  <LabeledValue
                    label="Est. Pickup"
                    value={prettyDate(inlandQuote?.estimated_pickup_at)}
                    defaultValue="-"
                    minColumnWidth={minColumnWidth}
                  />
                  <Stack pt={6}>
                    <SimpleForm onSubmit={handleSubmit} {...methods}>
                      <SimpleInput
                        name="trucking_ref"
                        label="TRUCKING REF (PRO#)"
                        placeholder="TRUCKING REF (PRO#)"
                        variant="outline"
                        w="full"
                      />
                      <Button type="submit" colorScheme="primary" size="lg" isLoading={isUpdating}>
                        Save
                      </Button>
                    </SimpleForm>
                  </Stack>
                </Stack>
                <Stack spacing="6" flex="1">
                  <Heading variant="h5">Drop Off</Heading>

                  <Stack>
                    <LabeledValue label="Address" value={dropOffAddressText} minColumnWidth="100px" defaultValue="-" />
                    <LabeledValue
                      label="Cut Off Date"
                      value={prettyDate(quote?.cutoff_at, {
                        showTime: true,
                        timeZone: quote?.origin_airport?.time_zone,
                      })}
                      minColumnWidth="100px"
                      defaultValue="-"
                    />
                    <LabeledValue
                      label="Earliest Tender"
                      value={earliestTenderOption}
                      defaultValue="-"
                      minColumnWidth="100px"
                    />
                    <LabeledValue
                      label="Drop Off Notes"
                      value={quote?.dropoff_instructions}
                      defaultValue="-"
                      minColumnWidth="100px"
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </CardBody>
        </Card>
        <RequestDetailsCard request={quote?.request} quote={quote} />
      </Stack>
      <Stack w="full" spacing="6" alignItems="flex-start">
        <Card h="fit-content">
          <CardHeader>
            <Heading variant="h5">Documents</Heading>
          </CardHeader>
          <CardBody p="4">
            <SimpleForm onSubmit={() => {}} {...(methods as any)}>
              <DocumentsForm name="documents" requestId={quote?.request?.id} documentTypes={documentTypes} />
            </SimpleForm>
          </CardBody>
        </Card>
        <Card h="fit-content">
          <CardBody p="4">
            <Stack spacing={6}>
              <Stack direction="row" align="center" justify="space-between" spacing="4" role="group">
                <Heading variant="h5">Cost Summary</Heading>
                <CopyLineItemsButton quote={quote} quoteVariant={bookedQuoteVariant} />
              </Stack>
              <QuoteVariantUI
                quote={quote}
                quoteVariant={bookedQuoteVariant}
                h="fit-content"
                showCopy={false}
                showTotal={false}
                showMarkupBreakdown
              />
              <Validity quote={quote} />
            </Stack>
          </CardBody>
        </Card>
        <Card borderRadius="lg" role="group" h="fit-content">
          <CardHeader py="2" pb="0">
            <Heading variant="h5">Airline & Flight Details</Heading>
          </CardHeader>
          <CardBody pb="0">
            <Stack>
              <Stack direction="row" align="center" spacing="6">
                <Image src={quote?.provider?.logo_uri} height="48px" width="48px" alt={quote?.provider?.name} />
                <Heading variant="h6">
                  {quote?.origin_airport_id}{' '}
                  <Box as="span" color="gray.400">
                    →
                  </Box>{' '}
                  {quote?.destination_airport_id}
                </Heading>
                <Text textStyle="bodySmallRegular">{stopCountLabel}</Text>
                <CopyButton text={buildFlightPathString(quote?.flight_path)} marginLeft="auto" />
              </Stack>
              <Text color="text.subtle" textStyle="captionSmallRegular">
                {quote?.provider?.name}
              </Text>
            </Stack>
            <Timeline data={flightData} />
          </CardBody>
        </Card>
      </Stack>
    </Stack>
  );
};
